import { createApiClient } from '../../../../../api/apiClient';
import { buildOrderRulesRequest } from './requestBuilder';
import { MaxPotentialExecutionResponse, OrderRulesDashboardResponse, OrderRulesFilterParams } from './types';

// ─── API: /api/order-rules/max-potential-execution ───────────────────────────

export const fetchOrderRulesMaxPotentialExecution = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
  pagination: { page: number; size: number },
): Promise<MaxPotentialExecutionResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds, pagination, { includeCustomerMaterialFilters: false });
  const res = await api.post<{ data: MaxPotentialExecutionResponse; success: boolean; message: string }>(
    '/order-rules/max-potential-execution',
    body,
  );
  // Endpoint wraps data in { success, message, data: { kpiBlocks, records, ... } }
  return (res.data as any)?.data ?? res.data;
};

// ─── Normalizer: backend → legacy dashboard shape ────────────────────────────

export const normalizeMaxPotentialExecutionResponse = (
  data: MaxPotentialExecutionResponse,
): Pick<OrderRulesDashboardResponse, 'maxPotential' | 'maxPotentialNoLayer' | 'maxPotentialKpiBlocks'> => {
  // Support both field name variants: API returns `records`, legacy shape used `rows`
  const rows = data.records ?? data.rows ?? [];

  return {
    maxPotential: rows.map((row) => ({
      region: row.region,
      market: row.market,
      buildLocation: row.buildLocation,
      locationId: row.locationId,
      skuSlots: row.skuSlots ?? 0,
      // Use the real API field; fall back to legacy alias
      lastUpdateOrderRules: row.lastUpdateOrderRules ?? row.lastUpdatedAt,
      // The API omits these fields entirely (rather than sending 0) for some rows.
      layerPerformancePct: row.layerPerformancePct ?? 0,
      maxPlanPct: row.maxPlanPct ?? 0,
      optimizedPlanPct: row.optimizedPlanPct ?? 0,
      maxPotentialPct: row.maxPotentialPct ?? 0,
      maxPotExecPct: row.maxPotExecPct ?? 0,
      // Use the real API field ($/wk); fall back to legacy alias
      estCostSavings: row.estCostSavings ?? row.annualSavingsUsd ?? 0,
      volume: row.volume ?? 0,
      linkedSites: (row.linkedSites ?? []).map((site) => ({
        location: site.location,
        skuSlots: site.skuSlots ?? 0,
        layerPerformancePct: site.layerPerformancePct ?? 0,
        maxPlanPct: site.maxPlanPct ?? 0,
        optimizedPlanPct: site.optimizedPlanPct ?? 0,
        maxPotentialPct: site.maxPotentialPct ?? 0,
        volume: site.volume ?? 0,
        siteType: site.siteType,
      })),
    })),
    maxPotentialNoLayer: [],
    // Pass the pre-computed KPI block through so the derived-data hook can use it
    maxPotentialKpiBlocks: data.kpiBlocks ?? data.kpis,
  };
};
